import { api, getAccessToken } from "@/lib/api/client";
import { employeeKeys } from "@/features/employees/api";

export type SalarySheetStatus =
  | "draft"
  | "with_accounts"
  | "with_wps"
  | "processed"
  | "cancelled";

/** Where a leave salary figure came from: HR's own, or a month's pay by the setting. */
export type LeaveSalarySource = "hr" | "basis";

/** "Set by HR" or "One month's pay", wherever a leave salary figure is shown. */
export function leaveSalarySourceLabel(source: LeaveSalarySource | null | undefined): string {
  return source === "hr" ? "Set by HR" : "One month's pay";
}

/**
 * A suggestion that somebody may be owed an evening shift incentive: the rota
 * put them on evenings in this month and the one before. Only a suggestion;
 * HR decides.
 */
export interface EveningHint {
  /** The two months, short names, oldest first: ["Sep", "Oct"]. */
  months: string[];
  /** Evenings in the sheet's own month. */
  evenings: number;
}

/** One person's pay for one month. */
export interface SalaryLine {
  id: string;
  user_id: string;
  employee_number: string | null;
  full_name: string;
  bank_name: string | null;
  iban: string | null;
  basic: string;
  allowances: string;
  /** The third pay component, and what HR said it was for. */
  other_pay: string;
  other_pay_note: string | null;
  /** The fourth component, optional, for whoever handles cash. */
  cash_risk: string;
  /** Basic, allowances, cash risk and other: the total salary. */
  gross: string;
  unpaid_days: string;
  half_paid_days: string;
  leave_deduction: string;
  /** The annual leave salary, in the month of the joining anniversary. */
  leave_salary: string;
  /**
   * "hr" when HR set the figure on the record, "basis" when it is a month's
   * pay by the company setting. Null in every other month.
   */
  leave_salary_source: LeaveSalarySource | null;
  /** Overtime HR chose to pay this month, in minutes, and what it comes to. */
  overtime_minutes: number;
  overtime_pay: string;
  /** AED an hour of overtime pays this person, already at the overtime percentage. */
  overtime_rate: string;
  /** What could be paid on this line: the balance, plus what the line already took. */
  overtime_banked_minutes: number;
  incentives: string;
  /** Evening shift incentives HR decided to pay, and what they came to. */
  evening_incentive_count: number;
  evening_incentive_amount: string;
  /** Only on a sheet still with HR, and only for somebody it may apply to. */
  evening_incentive_hint: EveningHint | null;
  /** The GPSSA pension contribution of a UAE national. Nought for everybody else. */
  gpssa_deduction: string;
  /** Whether the person is a UAE national, so the GPSSA can be changed on the sheet. */
  gpssa_applies?: boolean;
  other_deduction: string;
  other_addition: string;
  /**
   * gross + leave salary + overtime + incentives + evening incentives − leave
   * deduction − GPSSA − other deduction + other addition.
   */
  net: string;
  /** What the system says about the line, such as the annual leave salary. */
  auto_remarks: string[];
  note: string | null;
}

/**
 * One person's pay, changed from the sheet. It changes on their record, not
 * just on this month's line, so next month starts from it.
 */
export interface PayEdit {
  line_id: string;
  basic?: string;
  allowances?: string;
  cash_risk?: string;
  other_pay?: string;
  /** Empty takes the words off. */
  other_pay_note?: string;
  /** The GPSSA contribution of a UAE national, a month. */
  gpssa?: string;
}

/** Somebody Accounts may send an approved sheet to, and whether they are in today. */
export interface WpsChoice {
  user_id: string;
  name: string;
  employee_number: string | null;
  /** "WPS Officer", "Alternate WPS Officer", or "Named in Settings". */
  role: string;
  available: boolean;
  /** The leave they are on today, when they are away. */
  away: string | null;
}

/** HR's figures on one line. Only the ones sent change. */
export interface LineEdit {
  incentives?: string;
  /** How many evening shift incentives to pay, 0 to 31. Priced at today's rate. */
  evening_incentive_count?: number;
  /** Decimal hours of banked overtime to pay. "0" pays none. */
  overtime_hours?: string;
  other_deduction?: string;
  other_addition?: string;
  note?: string | null;
}

export interface SalarySheet {
  id: string;
  month: string;
  status: SalarySheetStatus;
  note: string | null;

  sent_to_accounts_at: string | null;
  sent_to_accounts_by_name: string | null;
  returned_at: string | null;
  returned_by_name: string | null;
  returned_note: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  processed_at: string | null;
  processed_by_name: string | null;
  /** Who Accounts sent it to to file: the WPS officer or the alternate. */
  wps_officer_id?: string | null;
  wps_officer_name?: string | null;

  people: number;
  total_gross: string;
  total_deductions: string;
  /** Overtime paid, incentives (evening ones too), additions and any annual leave salary. */
  total_additions: string;
  total_net: string;
  /** The overtime rule the month is priced with, e.g. "125" of the basic hourly rate. */
  overtime_percent: string;
  overtime_basis: "basic" | "gross";
  lines: SalaryLine[];

  /** What the reader may do next, decided by the API. */
  can_edit: boolean;
  can_send: boolean;
  can_decide: boolean;
  can_file: boolean;
  /** Accounts handing an approved sheet to the other WPS officer. */
  can_hand_to?: boolean;
}

/** Who the sheet goes to after HR, and who files it at the bank. */
export interface PayrollChain {
  accounts_user_ids: string[];
  wps_user_ids: string[];
  accounts_names: string[];
  wps_names: string[];
  /** Who files it by their position, without being named: "Name (WPS Officer)". */
  wps_by_position?: string[];
}

/**
 * The four pay components, as a change records them: "6000.00", or null for
 * none. And the GPSSA contribution, which the history follows too though it is
 * not part of the gross; changes from before it was followed do not carry it.
 */
export interface PayComponents {
  basic_salary: string | null;
  allowances: string | null;
  cash_risk: string | null;
  other_pay: string | null;
  gpssa_amount?: string | null;
}

/** One change to somebody's pay, with the reason given for it. */
export interface SalaryChange {
  id: string;
  user_id: string;
  employee_number: string | null;
  full_name: string;
  changed_at: string;
  effective_on: string | null;
  /**
   * "record" for an edit to the record, "review" for a salary review applied,
   * "sheet" for a change made from the salary sheet.
   */
  source: "record" | "review" | "sheet";
  reason: string | null;
  changed_by_name: string | null;
  before: PayComponents;
  after: PayComponents;
  gross_before: string;
  gross_after: string;
}

export const payrollKeys = {
  all: ["payroll"] as const,
  sheets: () => [...payrollKeys.all, "sheets"] as const,
  sheet: (id: string) => [...payrollKeys.all, "sheet", id] as const,
  chain: () => [...payrollKeys.all, "chain"] as const,
  wpsOfficers: (id: string) => [...payrollKeys.all, "wps-officers", id] as const,
};

/**
 * Pay changes, kept under the employees' keys rather than payroll's.
 *
 * A change is made by editing a record or applying a review, and both of
 * those already refresh everything under the employees' keys; living there,
 * the history is never a change behind the record it describes.
 */
export const salaryChangeKeys = {
  all: [...employeeKeys.all, "salary-changes"] as const,
  everyone: () => [...salaryChangeKeys.all, "everyone"] as const,
  forEmployee: (userId: string) => [...salaryChangeKeys.all, "one", userId] as const,
};

export const payrollApi = {
  sheets: () => api.get<SalarySheet[]>("/salary-sheets"),
  sheet: (id: string) => api.get<SalarySheet>(`/salary-sheets/${id}`),
  build: (month: string) => api.post<SalarySheet>("/salary-sheets", { month }),

  editLine: (sheetId: string, lineId: string, payload: LineEdit) =>
    api.patch<SalarySheet>(`/salary-sheets/${sheetId}/lines/${lineId}`, payload),

  changePay: (sheetId: string, changes: PayEdit[], reason: string) =>
    api.post<SalarySheet>(`/salary-sheets/${sheetId}/pay`, { changes, reason }),

  send: (id: string) => api.post<SalarySheet>(`/salary-sheets/${id}/send`),
  wpsOfficers: (id: string) => api.get<WpsChoice[]>(`/salary-sheets/${id}/wps-officers`),
  approve: (id: string, wpsUserId: string | null) =>
    api.post<SalarySheet>(`/salary-sheets/${id}/approve`, { wps_user_id: wpsUserId }),
  handTo: (id: string, wpsUserId: string) =>
    api.post<SalarySheet>(`/salary-sheets/${id}/hand-to`, { wps_user_id: wpsUserId }),
  sendBack: (id: string, note: string) =>
    api.post<SalarySheet>(`/salary-sheets/${id}/return`, { note }),
  markFiled: (id: string) => api.post<SalarySheet>(`/salary-sheets/${id}/processed`),

  salaryChanges: () => api.get<SalaryChange[]>("/salary-changes"),
  employeeSalaryChanges: (userId: string) =>
    api.get<SalaryChange[]>(`/employees/${userId}/salary-changes`),

  chain: () => api.get<PayrollChain>("/payroll-chain"),
  saveChain: (payload: { accounts_user_ids: string[]; wps_user_ids: string[] }) =>
    api.put<PayrollChain>("/payroll-chain", payload),
};

/** Where the export lives, for the download helper. */
export function exportPath(sheetId: string): string {
  return `/salary-sheets/${sheetId}/export.csv`;
}

/**
 * Save the month as a file.
 *
 * Fetched with the bearer token and handed to the browser as a blob, because
 * the export sits behind the same authorisation as everything else and a bare
 * link carries no token.
 */
export async function downloadSheet(sheetId: string, month: string): Promise<boolean> {
  const response = await fetch(`/api/v1${exportPath(sheetId)}`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    credentials: "include",
  });
  if (!response.ok) return false;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = `salaries-${month.slice(0, 7)}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}
